import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavDrawer, NavScrim, Sidebar as SidebarStyled } from '../../Styles/AppStyles';
import { IconButton } from '@mui/material';
import IconWrapper from './IconWrapper';
import {
  HomeIcon, InventoryIcon, TransactionsIcon,
  AdminIcon, SettingsIcon, LogoutIcon,
   CompanyIcon, LocationIcon,
  AssetsIcon, GPSIcon,
  UsersIcon, NonAbatixIcon, VendingMachineIcon
} from '../Icons';
import Logo from '../../assets/images/AIMS_Logo_white_TM.png';
import { COLORS } from '../../Styles/Colors';
import Cookies from 'js-cookie';
import apiClient from '../../Services/ApiClient';
import { decode } from '../../Services/DecodeToken';
import { Category, FireTruck, StorageOutlined, Widgets } from '@mui/icons-material';
import {
  Activity,
  AlertTriangle,
  LayoutDashboard,
  Link2,
  Package,
  Store,
  Users,
} from 'lucide-react';
import ReportsIcon from '../Icons/ReportsIcon';
import EmailIcon from '../Icons/EmailIcon';
import SubSidebarItem from './SubSidebarItem';
import SubSidebarPanel from './SubSidebarPanel';
import { isVendingEnabled } from '../../Services/VendingApi';
import { ChevronLeft, PackagePlus, Smartphone, Trash2Icon, X } from 'lucide-react';

type SubSidebarType = 'inventory' | 'assets' | 'admin' | 'settings' | 'non-abatix' | 'vending';

interface SidebarProps {
  compact?: boolean;
  open?: boolean;
  onClose?: () => void;
}

const SUB_SIDEBAR_CONFIG: Record<SubSidebarType, { title: string }> = {
  inventory: { title: 'Inventory' },
  admin: { title: 'Admin Panel' },
  assets: { title: 'Assets' },
  settings: { title: 'Settings' },
  'non-abatix': { title: 'Non-Abatix' },
  vending: { title: 'Vending' },
};

const Sidebar: React.FC<SidebarProps> = ({ compact = false, open = true, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const token: any = decode();
  const roleLevel = token.roles.level;
  const isAdminAccess = roleLevel >= 50;

  const [activeSubSidebar, setActiveSubSidebar] = useState<SubSidebarType | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const subSidebarPanelRef = useRef<HTMLDivElement>(null);
  const adminIconRef = useRef<HTMLDivElement>(null);
  const settingsIconRef = useRef<HTMLDivElement>(null);
  const inventoryIconRef = useRef<HTMLDivElement>(null);
  const assetsIconRef = useRef<HTMLDivElement>(null);
  const nonAbatixIconRef = useRef<HTMLDivElement>(null);
  const vendingIconRef = useRef<HTMLDivElement>(null);

  const anySubSidebarActive = activeSubSidebar !== null;

  const toggleSubSidebar = (type: SubSidebarType) => {
    if (activeSubSidebar === type && panelOpen) {
      setPanelOpen(false);
      return;
    }
    setActiveSubSidebar(type);
    requestAnimationFrame(() => setPanelOpen(true));
  };

  const closeSubSidebar = () => {
    setPanelOpen(false);
  };

  useEffect(() => {
    setPanelOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (compact && !open) {
      setPanelOpen(false);
    }
  }, [compact, open]);

  useEffect(() => {
    if (!panelOpen && activeSubSidebar) {
      const timer = window.setTimeout(() => setActiveSubSidebar(null), 280);
      return () => window.clearTimeout(timer);
    }
  }, [panelOpen, activeSubSidebar]);

  useEffect(() => {
    if (compact) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!panelOpen || !activeSubSidebar) return;

      const target = event.target as Node;
      const iconRefs = [inventoryIconRef, assetsIconRef, adminIconRef, settingsIconRef, nonAbatixIconRef, vendingIconRef];
      const clickedIcon = iconRefs.some(ref => ref.current?.contains(target));
      if (clickedIcon) return;

      if (subSidebarPanelRef.current && !subSidebarPanelRef.current.contains(target)) {
        closeSubSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelOpen, activeSubSidebar, compact]);

  const dismissNav = () => {
    closeSubSidebar();
    onClose?.();
  };

  const handleNavigation = (page: string) => {
    dismissNav();
    navigate(`/${page}`);
  };

  const handleAdminNavigation = (subpage: string) => {
    dismissNav();
    navigate(`/admin/${subpage}`);
  };

  const handleInventoryNavigation = (route: string) => {
    dismissNav();
    navigate(route);
  };

  const handleAssetsNavigation = (route: string) => {
    dismissNav();
    navigate(route);
  };

  const handleSettingsNavigation = (route: string) => {
    dismissNav();
    navigate(route);
  };

  const handleNonAbatixNavigation = (route: string) => {
    dismissNav();
    navigate(route);
  };

  const handleVendingNavigation = (route: string) => {
    dismissNav();
    navigate(route);
  };

  const handleLogout = async () => {
    onClose?.();
    await apiClient('/api/auth/logout', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${Cookies.get('token')}`,
      },
    });

    Cookies.remove("token");
    navigate('/');
  };

  const renderSubSidebarContent = () => {
    switch (activeSubSidebar) {
      case 'inventory':
        return (
          <>
            <SubSidebarItem
              onClick={() => handleInventoryNavigation('/inventory')}
              active={pathname === '/inventory'}
            >
              <InventoryIcon />
              <span>View Inventory</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleInventoryNavigation('/inventory/view-by-bin')}
              active={pathname === '/inventory/view-by-bin'}
            >
              <StorageOutlined />
              <span>View by Bin</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleInventoryNavigation('/reports')}
              active={pathname === '/reports'}
            >
              <ReportsIcon />
              <span>Inventory Audit Trail</span>
            </SubSidebarItem>

            {roleLevel >= 100 && (
              <SubSidebarItem
                onClick={() => handleInventoryNavigation('/admin/inventory/deleted')}
                active={pathname === '/admin/inventory/deleted'}
              >
                <Trash2Icon size={20} />
                <span>Deleted Items</span>
              </SubSidebarItem>
            )}
          </>
        );

      case 'admin':
        return (
          <>
            {[
              { label: 'Users', icon: <UsersIcon />, route: 'user-management' },
              { label: 'Locations', icon: <LocationIcon />, route: 'location-management' },
              ...(roleLevel >= 100 ? [{ label: 'Company', icon: <CompanyIcon />, route: 'company-management' }] : []),
              ...(roleLevel >= 100 ? [{ label: 'Customer Accounts', icon: <Smartphone size={20} />, route: 'customer-accounts' }] : []),
              { label: 'Bin', icon: <StorageOutlined />, route: 'inventory/bin-management' },
              { label: 'GPS Devices', icon: <GPSIcon />, route: 'gps-management' },
              { label: 'Fleet', icon: <FireTruck />, route: 'fleet-management' },
              { label: 'Kits', icon: <AssetsIcon />, route: 'kits-management' },
              ...(roleLevel >= 100 ? [{ label: 'Low Stock Failures', icon: <TransactionsIcon />, route: 'low-stock-failed-orders' }] : []),
              ...(roleLevel >= 100 ? [{ label: 'Failed Orders', icon: <TransactionsIcon />, route: 'failed-orders' }] : []),
              ...(roleLevel >= 100 ? [{ label: 'Usage Reports', icon: <ReportsIcon />, route: 'usage-reports' }] : []),
            ].map(item => (
              <SubSidebarItem
                key={item.route}
                onClick={() => handleAdminNavigation(item.route)}
                active={pathname === `/admin/${item.route}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </SubSidebarItem>
            ))}
          </>
        );

      case 'assets':
        return (
          <>
            <SubSidebarItem
              onClick={() => handleAssetsNavigation('/assets')}
              active={pathname === '/assets'}
            >
              <AssetsIcon />
              <span>View Assets</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleAssetsNavigation('/assets/transactions')}
              active={pathname === '/assets/transactions'}
            >
              <TransactionsIcon />
              <span>Asset Transactions</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleAssetsNavigation('/assets/category')}
              active={pathname === '/assets/category'}
            >
              <Category />
              <span>Manage Categories</span>
            </SubSidebarItem>
          </>
        );

      case 'settings':
        return (
          <>
            {token.roles.level >= 100 && (
              <SubSidebarItem
                onClick={() => handleSettingsNavigation('/emails')}
                active={pathname === '/emails'}
              >
                <EmailIcon />
                <span>Email Management</span>
              </SubSidebarItem>
            )}

            <SubSidebarItem
              onClick={() => handleSettingsNavigation('/settings/widgets')}
              active={pathname === '/settings/widgets'}
            >
              <Widgets />
              <span>Widgets</span>
            </SubSidebarItem>
          </>
        );

      case 'non-abatix':
        return (
          <>
            {/* Non-ABATIX items and transactions are on Inventory and Transactions (Type filter). */}
            <SubSidebarItem
              onClick={() => handleNonAbatixNavigation('/non-abatix/add-items')}
              active={pathname === '/non-abatix/add-items'}
            >
              <PackagePlus size={20} />
              <span>Add / Import Items</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleNonAbatixNavigation('/non-abatix/audit-trail')}
              active={pathname === '/non-abatix/audit-trail'}
            >
              <ReportsIcon />
              <span>Inventory Audit Trail</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleNonAbatixNavigation('/non-abatix/product-category')}
              active={pathname === '/non-abatix/product-category'}
            >
              <Category />
              <span>Manage Categories</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleNonAbatixNavigation('/non-abatix/deleted')}
              active={pathname === '/non-abatix/deleted'}
            >
              <Trash2Icon size={20} />
              <span>Deleted Items</span>
            </SubSidebarItem>
          </>
        );

      case 'vending': {
        const machineMatch = pathname.match(/^\/vending\/machines\/(\d+)/);
        const machineId = machineMatch?.[1];
        const machineBase = machineId ? `/vending/machines/${machineId}` : null;

        return (
          <>
            <SubSidebarItem
              onClick={() => handleVendingNavigation('/vending')}
              active={pathname === '/vending'}
            >
              <LayoutDashboard size={20} className="lucide" />
              <span>Dashboard</span>
            </SubSidebarItem>

            <SubSidebarItem
              onClick={() => handleVendingNavigation('/vending/machines')}
              active={pathname.startsWith('/vending/machines')}
            >
              <VendingMachineIcon />
              <span>Machines</span>
            </SubSidebarItem>

            {machineBase && (
              <>
                <SubSidebarItem
                  $nested
                  onClick={() => handleVendingNavigation(machineBase)}
                  active={pathname === machineBase}
                >
                  <LayoutDashboard size={18} className="lucide" />
                  <span>Overview</span>
                </SubSidebarItem>

                <SubSidebarItem
                  $nested
                  onClick={() => handleVendingNavigation(`${machineBase}/inventory`)}
                  active={pathname === `${machineBase}/inventory`}
                >
                  <InventoryIcon />
                  <span>Inventory</span>
                </SubSidebarItem>

                <SubSidebarItem
                  $nested
                  onClick={() => handleVendingNavigation(`${machineBase}/transactions`)}
                  active={pathname === `${machineBase}/transactions`}
                >
                  <TransactionsIcon />
                  <span>Transactions</span>
                </SubSidebarItem>
              </>
            )}

            {[
              { label: 'Transactions', icon: <TransactionsIcon />, route: '/vending/transactions', active: pathname === '/vending/transactions' },
              { label: 'Restock', icon: <Package size={20} className="lucide" />, route: '/vending/restock', active: pathname === '/vending/restock' },
              { label: 'Alerts', icon: <AlertTriangle size={20} className="lucide" />, route: '/vending/alerts', active: pathname === '/vending/alerts' },
              { label: 'Customers', icon: <Users size={20} className="lucide" />, route: '/vending/customers', active: pathname.startsWith('/vending/customers') },
              ...(roleLevel >= 100 ? [
                { label: 'Mappings', icon: <Link2 size={20} className="lucide" />, route: '/vending/mappings/machines', active: pathname.startsWith('/vending/mappings') },
                { label: 'Audit Log', icon: <Activity size={20} className="lucide" />, route: '/vending/audit', active: pathname === '/vending/audit' },
                { label: 'Vendor Companies', icon: <Store size={20} className="lucide" />, route: '/vending/vendor-companies', active: pathname === '/vending/vendor-companies' },
                { label: 'Integration Health', icon: <Activity size={20} className="lucide" />, route: '/vending/integration-health', active: pathname === '/vending/integration-health' || pathname === '/vending/integration' },
              ] : []),
            ].map((item) => (
              <SubSidebarItem
                key={item.route}
                onClick={() => handleVendingNavigation(item.route)}
                active={item.active}
              >
                {item.icon}
                <span>{item.label}</span>
              </SubSidebarItem>
            ))}
          </>
        );
      }

      default:
        return null;
    }
  };

  const showDrillIn = compact && panelOpen && !!activeSubSidebar;

  const navBody = (
    <>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '10px',
        paddingBottom: '20px',
        WebkitOverflowScrolling: 'touch',
      }}>
        {showDrillIn ? (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '8px 4px 16px',
            }}>
              <IconButton
                aria-label="Back to main menu"
                onClick={closeSubSidebar}
                sx={{ color: 'white', width: 44, height: 44 }}
              >
                <ChevronLeft size={22} />
              </IconButton>
              <span style={{ color: 'white', fontWeight: 700, fontSize: 16 }}>
                {activeSubSidebar ? SUB_SIDEBAR_CONFIG[activeSubSidebar].title : ''}
              </span>
            </div>
            {renderSubSidebarContent()}
          </>
        ) : (
          <>
            <div style={{
              padding: compact ? '8px 8px 12px' : '15px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: compact ? 'space-between' : 'center',
            }}>
              <img src={Logo} alt="Logo" style={{ width: compact ? '120px' : '150px' }} />
              {compact && (
                <IconButton
                  aria-label="Close navigation"
                  onClick={onClose}
                  sx={{ color: 'white', width: 44, height: 44 }}
                >
                  <X size={20} />
                </IconButton>
              )}
            </div>

            <IconWrapper active={!anySubSidebarActive && pathname === '/home'} onClick={() => handleNavigation('home')}>
              <HomeIcon />
              <span>Home</span>
            </IconWrapper>

            <IconWrapper
              active={activeSubSidebar === 'inventory' || (!anySubSidebarActive && (pathname.startsWith('/inventory') || pathname === '/reports' || pathname === '/admin/inventory/deleted'))}
              ref={inventoryIconRef}
              onClick={() => toggleSubSidebar('inventory')}
            >
              <InventoryIcon />
              <span>Inventory</span>
            </IconWrapper>

            <IconWrapper active={!anySubSidebarActive && pathname === '/transactions'} onClick={() => handleNavigation('transactions')}>
              <TransactionsIcon />
              <span>Transactions</span>
            </IconWrapper>

            <IconWrapper
              active={activeSubSidebar === 'non-abatix' || (!anySubSidebarActive && pathname.startsWith('/non-abatix'))}
              ref={nonAbatixIconRef}
              onClick={() => toggleSubSidebar('non-abatix')}
            >
              <NonAbatixIcon />
              <span>Non-Abatix</span>
            </IconWrapper>

            <IconWrapper
              active={activeSubSidebar === 'assets' || (!anySubSidebarActive && pathname.startsWith('/assets'))}
              ref={assetsIconRef}
              onClick={() => toggleSubSidebar('assets')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <AssetsIcon />
              <span>Assets</span>
            </IconWrapper>

            {isVendingEnabled && (
              <IconWrapper
                active={activeSubSidebar === 'vending' || (!anySubSidebarActive && pathname.startsWith('/vending'))}
                ref={vendingIconRef}
                onClick={() => toggleSubSidebar('vending')}
              >
                <VendingMachineIcon />
                <span>Vending</span>
              </IconWrapper>
            )}

            {isAdminAccess && (
              <IconWrapper
                active={activeSubSidebar === 'admin' || (!anySubSidebarActive && pathname.startsWith('/admin') && pathname !== '/admin/inventory/deleted')}
                ref={adminIconRef}
                onClick={() => toggleSubSidebar('admin')}
              >
                <AdminIcon />
                <span>Admin</span>
              </IconWrapper>
            )}

            <IconWrapper
              active={activeSubSidebar === 'settings' || (!anySubSidebarActive && (pathname.startsWith('/settings') || pathname === '/emails'))}
              ref={settingsIconRef}
              onClick={() => toggleSubSidebar('settings')}
            >
              <SettingsIcon />
              <span>Settings</span>
            </IconWrapper>
          </>
        )}
      </div>

      <div style={{
        padding: '10px',
        paddingBottom: '16px',
        marginTop: 'auto',
        borderTop: `1px solid ${COLORS.lightGray}20`
      }}>
        <IconWrapper active={false} onClick={handleLogout}>
          <LogoutIcon />
          <span>Logout</span>
        </IconWrapper>

        {!showDrillIn && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 16,
          }}>
            <img
              src={`${process.env.PUBLIC_URL}/abatix-logo-white.png`}
              alt="ABATIX"
              style={{ width: compact ? 120 : 140, height: 'auto' }}
            />
          </div>
        )}
      </div>
    </>
  );

  const sidebarPanel = (
    <SidebarStyled style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      width: compact ? 280 : undefined,
    }}>
      {navBody}
    </SidebarStyled>
  );

  if (compact) {
    return (
      <>
        {open && <NavScrim onClick={onClose} />}
        <NavDrawer $open={open} className="sidebar-container">
          {sidebarPanel}
        </NavDrawer>
      </>
    );
  }

  return (
    <div className="sidebar-container" style={{
      display: 'flex',
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      zIndex: 100,
    }}>
      {sidebarPanel}

      {activeSubSidebar && (
        <SubSidebarPanel
          open={panelOpen}
          title={SUB_SIDEBAR_CONFIG[activeSubSidebar].title}
          contentKey={activeSubSidebar}
          panelRef={subSidebarPanelRef}
        >
          {renderSubSidebarContent()}
        </SubSidebarPanel>
      )}

      <style>{`
        .sidebar-container > div:first-child::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-container > div:first-child::-webkit-scrollbar-track {
          background: ${COLORS.darkGray};
        }

        .sidebar-container > div:first-child::-webkit-scrollbar-thumb {
          background: ${COLORS.lightGray};
          border-radius: 3px;
        }

        .sidebar-container > div:first-child::-webkit-scrollbar-thumb:hover {
          background: ${COLORS.teal};
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
